import { useMemo } from 'react';
import PropTypes from 'prop-types';
import Chart from 'react-apexcharts';

import { useTheme } from '#/hooks/useTheme';
import { cn } from '#/lib/utils';
import {
  DASHBOARD_CHART_CLASS,
  formatCount,
  getStatusColor,
} from '#/modules/infra/dashboard/helper/dashboardChartTheme';

/**
 * 各月進度（ApexCharts 橫向 100% 堆疊）：每列＝該月建立的單據，依目前狀態的「階段」分段。
 * `status_by_month` 以單據日期歸月、統計目前狀態，所以讀法是「那個月那批單現在走到哪」。
 * - 階段由 module 設定 `phases` 合併；response 沒有任何成員狀態的階段整段不顯示（財務權限裁切）。
 * - 段內標筆數（0 不標），列標籤附「共 N 筆」；tooltip 列出該段的細分狀態。
 * - 點色段 → `onBarClick({ status: 'A,B', month })`（search route 支援逗號 OR）。
 */
const StatusProgressBars = ({ statusByMonth, statusLabels, phases, onBarClick }) => {
  const { resolvedTheme } = useTheme();
  const months = useMemo(() => statusByMonth?.categories ?? [], [statusByMonth]);

  const { visiblePhases, series, totals } = useMemo(() => {
    const byStatus = Object.fromEntries((statusByMonth?.series ?? []).map((item) => [item.name, item.data ?? []]));
    const present = phases.filter((phase) => phase.statuses.some((status) => byStatus[status]));
    const phaseSeries = present.map((phase) => ({
      name: phase.label,
      data: months.map((_month, index) => phase.statuses.reduce(
        (sum, status) => sum + (Number(byStatus[status]?.[index]) || 0),
        0,
      )),
      breakdown: months.map((_month, index) => phase.statuses
        .filter((status) => byStatus[status])
        .map((status) => ({ status, count: Number(byStatus[status][index]) || 0 }))),
    }));
    return {
      visiblePhases: present,
      series: phaseSeries,
      totals: months.map((_month, index) => phaseSeries.reduce((sum, item) => sum + item.data[index], 0)),
    };
  }, [months, phases, statusByMonth]);

  const options = useMemo(() => {
    const phaseOrder = visiblePhases.map((phase) => phase.key);
    const phaseTones = Object.fromEntries(visiblePhases.map((phase) => [phase.key, phase.tone]));
    const colors = visiblePhases.map((phase) => getStatusColor(resolvedTheme, phase.key, {
      statusOrder: phaseOrder,
      statusTones: phaseTones,
    }));
    const rowLabel = (index) => `${months[index]} · 共 ${formatCount(totals[index])} 筆`;

    return {
      chart: {
        type: 'bar',
        stacked: true,
        stackType: '100%',
        background: 'transparent',
        toolbar: { show: false },
        zoom: { enabled: false },
        fontFamily: 'inherit',
        events: {
          dataPointSelection: (_event, _context, { seriesIndex, dataPointIndex }) => {
            const phase = visiblePhases[seriesIndex];
            const month = months[dataPointIndex];
            if (onBarClick && phase && month) {
              onBarClick({ status: phase.statuses.join(','), month });
            }
          },
        },
      },
      theme: { mode: resolvedTheme },
      colors,
      plotOptions: { bar: { horizontal: true, barHeight: '55%' } },
      dataLabels: {
        enabled: true,
        formatter: (_value, { seriesIndex, dataPointIndex }) => {
          const count = series[seriesIndex]?.data[dataPointIndex] ?? 0;
          return count > 0 ? formatCount(count) : '';
        },
        style: { fontSize: '12px', fontWeight: 600 },
        dropShadow: { enabled: false },
      },
      stroke: { show: true, width: 1, colors: ['transparent'] },
      xaxis: {
        categories: months.map((_month, index) => rowLabel(index)),
        min: 0,
        max: 100,
        tickAmount: 4,
        labels: { formatter: (value) => `${Math.round(value)}%` },
      },
      // 橫向長條的數值軸範圍由 yaxis min/max 決定；固定 0–100，避免整列為 0 時自動延伸成 -100%～100%
      yaxis: { min: 0, max: 100, tickAmount: 4, labels: { maxWidth: 200 } },
      grid: { strokeDashArray: 3 },
      legend: { position: 'top', horizontalAlign: 'left', markers: { shape: 'circle' } },
      tooltip: {
        shared: false,
        intersect: true,
        custom: ({ seriesIndex, dataPointIndex }) => {
          const item = series[seriesIndex];
          const count = item?.data[dataPointIndex] ?? 0;
          const total = totals[dataPointIndex] ?? 0;
          const percent = total > 0 ? Math.round((count / total) * 100) : 0;
          const rows = (item?.breakdown[dataPointIndex] ?? [])
            .filter((row) => row.count > 0)
            .map((row) => `
              <div class="flex gap-3 px-3 py-0.5">
                <span>${statusLabels[row.status] ?? row.status}</span>
                <span class="ml-auto tabular-nums">${formatCount(row.count)} 筆</span>
              </div>`)
            .join('');
          return `
            <div class="py-1.5 text-xs">
              <div class="px-3 pb-1 text-muted-foreground">${months[dataPointIndex] ?? ''}</div>
              <div class="flex items-center gap-2 px-3 pb-0.5 font-semibold">
                <span class="size-2 rounded-full" style="background:${colors[seriesIndex]}"></span>
                <span>${item?.name ?? ''}</span>
                <span class="ml-auto pl-3 tabular-nums">${formatCount(count)} 筆（${percent}%）</span>
              </div>
              ${rows}
            </div>`;
        },
      },
      states: { active: { filter: { type: 'none' } } },
    };
  }, [months, onBarClick, resolvedTheme, series, statusLabels, totals, visiblePhases]);

  if (months.length === 0) {
    return <p className="py-8 text-center text-sm text-muted-foreground">此區間沒有資料</p>;
  }

  return (
    <div className={cn(DASHBOARD_CHART_CLASS, onBarClick && '[&_.apexcharts-bar-area]:cursor-pointer')}>
      <Chart
        key={`${resolvedTheme}-${months.length}`}
        type="bar"
        height={Math.max(160, months.length * 56 + 90)}
        series={series.map(({ name, data }) => ({ name, data }))}
        options={options}
      />
    </div>
  );
};

StatusProgressBars.propTypes = {
  statusByMonth: PropTypes.shape({
    categories: PropTypes.arrayOf(PropTypes.string),
    series: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string,
        data: PropTypes.arrayOf(PropTypes.number),
      }),
    ),
  }),
  statusLabels: PropTypes.object.isRequired,
  phases: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      tone: PropTypes.string.isRequired,
      statuses: PropTypes.arrayOf(PropTypes.string).isRequired,
    }),
  ).isRequired,
  onBarClick: PropTypes.func,
};

export default StatusProgressBars;
